import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppLayout } from "@/components/layout/AppLayout";
import { AuthGate } from "@/components/AuthGate";
import { LoginPage } from "@/pages/LoginPage";
// Hot landing pages stay eager so the first navigation after login is
// instant. Heavier pages (with ECharts, dnd-kit, qrcode.react, etc.) are
// loaded on demand so the initial bundle stays small.
import { AccountsPage } from "@/pages/AccountsPage";
import { ServersPage } from "@/pages/ServersPage";
import { JobsPage } from "@/pages/JobsPage";
import { SnapshotsPage } from "@/pages/SnapshotsPage";
import { RestorePage } from "@/pages/RestorePage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { ExcludesPage } from "@/pages/ExcludesPage";
import { AuditPage } from "@/pages/AuditPage";
import { Skeleton } from "@/components/ui/skeleton";

// Each lazy() needs `default: Component` — our pages use named exports, so
// adapt them in the resolver. Vite/Rollup will emit one chunk per import().
const lazyNamed = <K extends string>(
  loader: () => Promise<Record<K, React.ComponentType>>,
  name: K,
) => lazy(async () => ({ default: (await loader())[name] }));

const DashboardPage = lazyNamed(() => import("@/pages/DashboardPage"), "DashboardPage"); // echarts
const StoragePage = lazyNamed(() => import("@/pages/StoragePage"), "StoragePage"); // echarts
const SettingsPage = lazyNamed(() => import("@/pages/SettingsPage"), "SettingsPage"); // qrcode.react
const GroupsPage = lazyNamed(() => import("@/pages/GroupsPage"), "GroupsPage"); // dnd-kit
const OpsPage = lazyNamed(() => import("@/pages/OpsPage"), "OpsPage");
const ServerDetailPage = lazyNamed(() => import("@/pages/ServerDetailPage"), "ServerDetailPage");
const AccountDetailPage = lazyNamed(() => import("@/pages/AccountDetailPage"), "AccountDetailPage");
const JobDetailPage = lazyNamed(() => import("@/pages/JobDetailPage"), "JobDetailPage");
const GraveyardPage = lazyNamed(() => import("@/pages/GraveyardPage"), "GraveyardPage");

function PageFallback() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <AuthGate>
            <AppLayout />
          </AuthGate>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route
          path="dashboard"
          element={
            <Suspense fallback={<PageFallback />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route path="servers" element={<ServersPage />} />
        <Route
          path="servers/:id"
          element={
            <Suspense fallback={<PageFallback />}>
              <ServerDetailPage />
            </Suspense>
          }
        />
        <Route path="accounts" element={<AccountsPage />} />
        <Route
          path="accounts/:id"
          element={
            <Suspense fallback={<PageFallback />}>
              <AccountDetailPage />
            </Suspense>
          }
        />
        <Route
          path="groups"
          element={
            <Suspense fallback={<PageFallback />}>
              <GroupsPage />
            </Suspense>
          }
        />
        <Route path="excludes" element={<ExcludesPage />} />
        <Route
          path="storage"
          element={
            <Suspense fallback={<PageFallback />}>
              <StoragePage />
            </Suspense>
          }
        />
        <Route path="jobs" element={<JobsPage />} />
        <Route
          path="jobs/:id"
          element={
            <Suspense fallback={<PageFallback />}>
              <JobDetailPage />
            </Suspense>
          }
        />
        <Route path="snapshots" element={<SnapshotsPage />} />
        <Route
          path="graveyard"
          element={
            <Suspense fallback={<PageFallback />}>
              <GraveyardPage />
            </Suspense>
          }
        />
        <Route
          path="ops"
          element={
            <Suspense fallback={<PageFallback />}>
              <OpsPage />
            </Suspense>
          }
        />
        <Route path="restore" element={<RestorePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route
          path="settings"
          element={
            <Suspense fallback={<PageFallback />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route path="audit" element={<AuditPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
